/**
 * Assistant Files Database Layer
 *
 * Supports browser and server clients (platform tools run on the server).
 */

import { TablesInsert } from '@/supabase/types'
import type { SupabaseClient } from '@supabase/supabase-js'
import { createServiceClient } from '@/lib/supabase/service-client'

type AssistantsClient = SupabaseClient<any>

const getDefaultClient = (client?: AssistantsClient): AssistantsClient => {
  if (client) return client

  if (typeof window !== 'undefined') {
    const { supabase } = require('@/lib/supabase/browser-client') as {
      supabase: AssistantsClient
    }
    return supabase
  }

  return createServiceClient()
}

export const getAssistantFilesByAssistantId = async (
  assistantId: string,
  client?: AssistantsClient
) => {
  const { data: assistantFiles, error } = await getDefaultClient(client)
    .from('assistants')
    .select(
      `
        id, 
        name, 
        files (*)
      `
    )
    .eq('id', assistantId)
    .single()

  if (!assistantFiles) {
    throw new Error(error?.message || 'Assistant files not found')
  }

  return assistantFiles
}

export type AssistantFileLink = {
  assistant_id: string
  file_id: string
  user_id?: string | null
  file?: {
    id: string
    name: string | null
    type: string | null
    description: string | null
  } | null
}

/** Soft list for context packs / attachments API — never throws. */
export async function listAssistantFileLinks(
  client: AssistantsClient,
  assistantId: string
): Promise<AssistantFileLink[]> {
  const { data, error } = await client
    .from('assistant_files')
    .select(
      `
      assistant_id,
      file_id,
      user_id,
      file:files (
        id, name, type, description
      )
    `
    )
    .eq('assistant_id', assistantId)

  if (error) {
    console.error('Error listing assistant files:', error)
    return []
  }

  return (data || []) as unknown as AssistantFileLink[]
}

export const createAssistantFile = async (
  assistantFile: TablesInsert<'assistant_files'>,
  client?: AssistantsClient
) => {
  const { data: createdAssistantFile, error } = await getDefaultClient(client)
    .from('assistant_files')
    .insert(assistantFile)
    .select('*')

  if (!createdAssistantFile) {
    throw new Error(error?.message || 'Failed to create assistant file')
  }

  return createdAssistantFile
}

export const createAssistantFiles = async (
  assistantFiles: TablesInsert<'assistant_files'>[],
  client?: AssistantsClient
) => {
  const { data: createdAssistantFiles, error } = await getDefaultClient(client)
    .from('assistant_files')
    .insert(assistantFiles)
    .select('*')

  if (!createdAssistantFiles) {
    throw new Error(error?.message || 'Failed to create assistant files')
  }

  return createdAssistantFiles
}

export const deleteAssistantFile = async (
  assistantId: string,
  fileId: string,
  client?: AssistantsClient
) => {
  const { error } = await getDefaultClient(client)
    .from('assistant_files')
    .delete()
    .eq('assistant_id', assistantId)
    .eq('file_id', fileId)

  if (error) throw new Error(error.message)

  return true
}
